import { useController, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { HEADQUARTERS_NAME } from '@speeki/dictionary';

import {
    SubsidiarySiteFromUrlDocument,
    SubsidiarySitesSettingsDocument,
} from '@graphql/generated/graphql';

import { useQuery, useReactiveVar } from '@apollo/client';
import { authVar, subsidiarySiteUuidFromUrlVar } from '@boot/reactiveVariables';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSubsidiarySitesSelect } from '@shared/selects/useSubsidiarySitesSelect';
import { requiredSelectV2 } from '@shared/validations';

export const useSelectSubsidiarySitesModal = () => {
    const isLoggedIn = useReactiveVar(authVar).isLoggedIn;
    const subsidiarySiteUuidFromUrl = useReactiveVar(
        subsidiarySiteUuidFromUrlVar,
    );
    const {
        data: subsidiarySitesSettingsData,
        loading: subsidiarySettingsLoading,
    } = useQuery(SubsidiarySitesSettingsDocument, {
        fetchPolicy: 'cache-and-network',
    });

    const subsidiarySiteCopy =
        subsidiarySitesSettingsData?.tenant.reportSettings
            ?.subsidiarySelectionCopy;
    const subsidiarySiteChoice =
        subsidiarySitesSettingsData?.tenant.reportSettings
            ?.subsidiaryChoiceEnabled;
    const tenantUuid = subsidiarySitesSettingsData?.tenant.uuid;

    const { data: subsidiarySiteFromUrlData } = useQuery(
        SubsidiarySiteFromUrlDocument,
        {
            fetchPolicy: 'cache-and-network',
            skip:
                subsidiarySettingsLoading ||
                !tenantUuid ||
                !!(subsidiarySiteChoice && !subsidiarySiteUuidFromUrl),
            variables: {
                input: {
                    subsidiaryUuid: subsidiarySiteUuidFromUrl || null,
                    tenantUuid: tenantUuid ?? '',
                },
                tenantUuid: isLoggedIn ? undefined : tenantUuid,
            },
        },
    );

    const subsidiarySiteSelect = useSubsidiarySitesSelect({
        variables: { input: { withHeadquarters: true } },
    });

    const {
        control,
        formState: { errors, isValid },
        handleSubmit,
        reset,
        setValue,
    } = useForm<SelectSubsidiarySiteModalValues>({
        defaultValues: {
            investigationEntity:
                subsidiarySiteChoice && !subsidiarySiteUuidFromUrl
                    ? 'HEADQUARTERS'
                    : undefined,
            subsidiarySite: undefined,
        },
        mode: 'onChange',
        resolver: yupResolver(SelectSubsidiarySiteModalSchema),
        values: {
            investigationEntity:
                subsidiarySiteChoice && !subsidiarySiteUuidFromUrl
                    ? 'HEADQUARTERS'
                    : undefined,
            subsidiaryChoiceEnabled: !!subsidiarySiteChoice,
            // @ts-expect-error WRONG TYPES
            subsidiarySite:
                (subsidiarySiteChoice && !!subsidiarySiteUuidFromUrl) ||
                !subsidiarySiteChoice
                    ? {
                          label:
                              (subsidiarySiteFromUrlData?.subsidiarySite
                                  ?.isHeadquarters
                                  ? subsidiarySiteFromUrlData?.subsidiarySite
                                        ?.reportingLabel
                                  : subsidiarySiteFromUrlData?.subsidiarySite
                                        ?.name) || '',
                          value:
                              subsidiarySiteFromUrlData?.subsidiarySite?.uuid ||
                              HEADQUARTERS_NAME,
                      }
                    : undefined,
        },
    });

    const { field: investigationEntityField } = useController({
        control,
        name: 'investigationEntity',
    });

    const { field: subsidiarySitesField } = useController({
        control,
        name: 'subsidiarySite',
    });

    return {
        control,
        errors,
        handleSubmit,
        investigationEntityField,
        isValid,
        reset,
        setValue,
        subsidiarySettingsLoading,
        subsidiarySiteChoice,
        subsidiarySiteCopy,
        subsidiarySiteSelect,
        subsidiarySitesField,
        tenantUuid,
    };
};

const SelectSubsidiarySiteModalSchema = yup.object({
    investigationEntity: yup.string().when('subsidiaryChoiceEnabled', {
        is: true,
        then: () => yup.string().required('REQUIRED'),
    }),
    subsidiaryChoiceEnabled: yup.boolean().required('REQUIRED'),
    subsidiarySite: requiredSelectV2,
});

export type SelectSubsidiarySiteModalValues = yup.InferType<
    typeof SelectSubsidiarySiteModalSchema
>;
